/**
 * Agent profile images.
 *
 * Images live in Cloud Storage and the agent document holds only a path. They
 * used to be base64 data URLs stored in the document itself, which meant a 2 MB
 * upload became ~2.7 MB of base64 - over Firestore's 1 MiB document limit, so
 * the save failed - and every read of an agent record carried the encoded image
 * along with it, including the roster query behind the management table.
 *
 * Objects are private; reads are short-lived signed URLs fetched in one batch
 * per screen rather than one request per row.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

/** Matches the server-side ceiling in func-agentProfileImages.ts. */
export const MAX_PROFILE_IMAGE_BYTES = 2 * 1024 * 1024;
export const ALLOWED_PROFILE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const uploadImage = httpsCallable<
	{ targetAgentId?: string; fileData: string; fileType: string },
	{ success: boolean; storagePath: string; url: string | null }
>(functions, 'uploadAgentProfileImage');

const readImageUrls = httpsCallable<
	{ agentIds: string[] },
	{ urls: Record<string, string> }
>(functions, 'getAgentProfileImageUrls');

const removeImage = httpsCallable<{ targetAgentId?: string }, { success: boolean }>(
	functions,
	'deleteAgentProfileImage'
);

export const formatFileSize = (bytes: number): string =>
	bytes >= 1024 * 1024
		? `${(bytes / 1024 / 1024).toFixed(1)} MB`
		: `${Math.round(bytes / 1024)} KB`;

/**
 * Reads a File as bare base64, without the `data:<type>;base64,` prefix the
 * server does not want.
 */
export const readFileAsBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result || '');
			const comma = result.indexOf(',');
			if (comma === -1) {
				reject(new Error('Could not read that image file.'));
				return;
			}
			resolve(result.slice(comma + 1));
		};
		// The old implementation had no error handler at all, so an unreadable
		// file left the dialog spinning with no message.
		reader.onerror = () => reject(new Error('Could not read that image file.'));
		reader.readAsDataURL(file);
	});

export class ProfileImageService {
	/** Validates locally so the user gets a fast, specific message. */
	static validate(file: File): string | null {
		if (!ALLOWED_PROFILE_IMAGE_TYPES.includes(file.type)) {
			return 'Choose a JPEG, PNG, or WebP image.';
		}
		if (file.size > MAX_PROFILE_IMAGE_BYTES) {
			return `That image is ${formatFileSize(file.size)}. The limit is ${formatFileSize(MAX_PROFILE_IMAGE_BYTES)}.`;
		}
		return null;
	}

	/** Uploads and returns a signed URL for immediate display. */
	static async upload(file: File, targetAgentId?: string): Promise<string | null> {
		const problem = ProfileImageService.validate(file);
		if (problem) {
			throw new Error(problem);
		}

		const fileData = await readFileAsBase64(file);
		const result = await uploadImage({
			...(targetAgentId ? { targetAgentId } : {}),
			fileData,
			fileType: file.type
		});

		return result.data.url;
	}

	/** One call per screen, not one per row. */
	static async getUrls(agentIds: string[]): Promise<Record<string, string>> {
		const unique = Array.from(new Set(agentIds.filter(Boolean)));
		if (unique.length === 0) return {};

		const result = await readImageUrls({ agentIds: unique });
		return result.data.urls || {};
	}

	static async remove(targetAgentId?: string): Promise<void> {
		await removeImage(targetAgentId ? { targetAgentId } : {});
	}
}
